import { useState, type ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { Icon } from "@/components/common/Icon";
import { ENVIRONMENT_ROOT_VOLUME_GIB, formatEnvironmentSize, type Environment } from "@/lib/api";
import { formatDiskUsage } from "@/lib/format-bytes";
import { formatRelativeDateTime } from "@/lib/format-datetime";
import { EditableEnvironmentName } from "./EditableEnvironmentName";
import { DiskUsageBar } from "./DiskUsageBar";
import { ExpandRootVolumeDialog } from "./ExpandRootVolumeDialog";
import { useToastStore } from "@/stores/useToastStore";
import { useHasPermission } from "@/lib/permissions";

interface EnvironmentDetailsPanelProps {
	environment: Environment;
	/** 環境 Pod が存在するか。無い間はインスタンスタイプが決まっていないので出さない。 */
	podExists: boolean;
	/** 名前の変更やボリュームの拡張に成功したら更新後の環境を通知する。 */
	onUpdated: (updated: Environment) => void;
}

/**
 * エージェントの名前・サマリーと基本情報を並べる。
 * デバイスの環境はデバイスのディスクで動くため、ルートボリュームの容量とその拡張は出さない。
 */
export function EnvironmentDetailsPanel({
	environment,
	podExists,
	onUpdated,
}: EnvironmentDetailsPanelProps) {
	const [expandVolumeOpen, setExpandVolumeOpen] = useState(false);
	const canManageAgents = useHasPermission("manageAgents");
	const onRunner = environment.runner != null;
	const volumeAtMax = environment.rootVolumeGib >= ENVIRONMENT_ROOT_VOLUME_GIB.max;

	return (
		<div
			data-testid="environment-details-panel"
			style={{
				flex: 1,
				minHeight: 0,
				padding: "20px 24px",
				display: "flex",
				flexDirection: "column",
				gap: 16,
			}}
		>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
				<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
					{canManageAgents ? (
						<EditableEnvironmentName environment={environment} onRenamed={onUpdated} />
					) : (
						<h4
							title={environment.name}
							data-tooltip-if-truncated
							style={{
								margin: 0,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								fontSize: "var(--text-heading)",
								fontWeight: 600,
								lineHeight: "var(--leading-normal)",
								color: "var(--fg-1)",
							}}
						>
							{environment.name}
						</h4>
					)}
					{/* サマリーの保存・生成は未対応。対応するまでは書く場所だけを示す。 */}
					<p
						data-testid="environment-summary-placeholder"
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							lineHeight: "var(--leading-body)",
							color: "var(--fg-3)",
						}}
					>
						このエージェントのサマリーはまだありません。担当する作業や目的をここにまとめられるようになります。
					</p>
				</div>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 80,
						height: 80,
						flexShrink: 0,
						borderRadius: "var(--radius-3)",
						background: "var(--surface-2)",
						color: "var(--fg-2)",
					}}
				>
					<Icon name="agent" size={44} />
				</span>
			</div>
			<dl
				style={{
					margin: "8px 0 12px",
					display: "grid",
					gridTemplateColumns: "140px minmax(0, 1fr)",
					rowGap: 6,
					columnGap: 16,
					fontSize: "var(--text-ui)",
				}}
			>
				<DetailRow label="インスタンスタイプ">
					{podExists ? formatEnvironmentSize(environment) : "--"}
				</DetailRow>
				<DetailRow label={onRunner ? "ディスク" : "ルートボリューム"}>
					<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
						<div
							style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, maxWidth: 320 }}
						>
							<div
								style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}
							>
								{!onRunner && (
									<span data-testid={`environment-root-volume-${environment.id}`}>
										{environment.rootVolumeGib} GiB
									</span>
								)}
								<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
									使用量 {formatDiskUsage(environment.diskUsedBytes, environment.diskTotalBytes)}
								</span>
							</div>
							<DiskUsageBar
								usedBytes={environment.diskUsedBytes}
								totalBytes={environment.diskTotalBytes}
							/>
						</div>
						{!onRunner && canManageAgents && (
							// ConfirmButton は title を持たないため、無効の理由は外側の要素に付ける。
							<span
								title={
									volumeAtMax ? "上限の容量まで拡張済みのため、これ以上拡張できません。" : undefined
								}
							>
								<ConfirmButton
									label="拡張"
									loading={false}
									variant="secondary"
									size="sm"
									disabled={volumeAtMax}
									onClick={() => setExpandVolumeOpen(true)}
									data-testid={`environment-expand-volume-${environment.id}`}
								/>
							</span>
						)}
					</div>
				</DetailRow>
				<DetailRow label="作成">
					<span style={{ fontVariantNumeric: "tabular-nums" }}>
						{formatRelativeDateTime(environment.createdAt)}
					</span>
				</DetailRow>
			</dl>
			<ExpandRootVolumeDialog
				open={expandVolumeOpen}
				environment={environment}
				onClose={() => setExpandVolumeOpen(false)}
				onExpanded={(expanded) => {
					onUpdated(expanded);
					useToastStore.getState().show({
						type: "success",
						message: `エージェント「${expanded.name}」のルートボリュームを ${expanded.rootVolumeGib} GiB に拡張しました。`,
					});
				}}
			/>
		</div>
	);
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
	return (
		<>
			<dt style={{ color: "var(--fg-3)", fontSize: "var(--text-caption)", fontWeight: 500 }}>
				{label}
			</dt>
			<dd style={{ margin: 0, minWidth: 0, color: "var(--fg-1)" }}>{children}</dd>
		</>
	);
}
